import React, { useState, useMemo } from 'react';
import { Beaker, Droplets, Syringe, Calculator, Info, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PeptiCoolBanner } from '../components/pepticool/PeptiCoolBanner';
const PEPTIDE_PRESETS = [
  { name: 'Custom', vial: '', water: '', dose: '', category: '' },
  { name: 'BPC-157', vial: 5, water: 2, dose: 250, category: 'Healing' },
  { name: 'TB-500', vial: 5, water: 2, dose: 2500, category: 'Healing' },
  { name: 'CJC-1295 / Ipamorelin', vial: 5, water: 2.5, dose: 300, category: 'GH Secretagogue' },
  { name: 'GHK-Cu', vial: 5, water: 2, dose: 200, category: 'Healing' },
  { name: 'MOTS-C', vial: 10, water: 2, dose: 5000, category: 'Metabolic' },
  { name: 'Tesamorelin', vial: 2, water: 2, dose: 2000, category: 'GH / Fat Loss' },
  { name: 'Epitalon', vial: 10, water: 2, dose: 5000, category: 'Anti-Aging' },
  { name: 'KPV', vial: 5, water: 2, dose: 500, category: 'Anti-Inflammatory' },
  { name: 'Dihexa', vial: 5, water: 2, dose: 500, category: 'Nootropic' },
  { name: 'PT-141', vial: 10, water: 2, dose: 2000, category: 'Libido' },
  { name: 'SLU-PP-332', vial: 10, water: 2, dose: 5000, category: 'Exercise Mimetic' },
  { name: 'Retatrutide', vial: 5, water: 2, dose: 500, category: 'Metabolic' },
];

const SyringeVisual = ({ fillPercent }) => {
  const clamp = Math.min(Math.max(fillPercent, 0), 100);
  return (
    <div className="relative w-full max-w-[280px] mx-auto my-8">
      {/* Syringe barrel */}
      <div className="relative h-10 bg-zinc-800 border-2 border-zinc-600 rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E63946] to-[#E63946]/70 transition-all duration-700 ease-out rounded-full"
          style={{ width: `${clamp}%` }}
        />
        {/* Tick marks */}
        {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(tick => (
          <div key={tick} className="absolute top-0 bottom-0 w-px bg-zinc-600/50" style={{ left: `${tick}%` }} />
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-sm font-bold drop-shadow-lg font-heading">
            {clamp > 0 ? `${clamp.toFixed(1)}%` : '—'}
          </span>
        </div>
      </div>
      {/* Labels */}
      <div className="flex justify-between mt-1 px-1">
        <span className="text-zinc-600 text-xs">0</span>
        <span className="text-zinc-600 text-xs">25</span>
        <span className="text-zinc-600 text-xs">50</span>
        <span className="text-zinc-600 text-xs">75</span>
        <span className="text-zinc-600 text-xs">100 units</span>
      </div>
    </div>
  );
};

export function PeptideCalculator({ compact = false }) {
  const [preset, setPreset] = useState('Custom');
  const [vialMg, setVialMg] = useState('');
  const [waterMl, setWaterMl] = useState('');
  const [doseMcg, setDoseMcg] = useState('');

  const applyPreset = (name) => {
    setPreset(name);
    const p = PEPTIDE_PRESETS.find(pp => pp.name === name);
    if (p) {
      setVialMg(p.vial || '');
      setWaterMl(p.water || '');
      setDoseMcg(p.dose || '');
    }
  };

  const result = useMemo(() => {
    const v = parseFloat(vialMg);
    const w = parseFloat(waterMl);
    const d = parseFloat(doseMcg);
    if (!v || !w || !d || v <= 0 || w <= 0 || d <= 0) return null;

    const concentrationMcgPerMl = (v * 1000) / w;
    const volumeToDrawMl = d / concentrationMcgPerMl;
    const syringeUnits = volumeToDrawMl * 100; // 100-unit insulin syringe

    return {
      concentration: concentrationMcgPerMl,
      volumeMl: volumeToDrawMl,
      units: syringeUnits,
      fillPercent: syringeUnits,
    };
  }, [vialMg, waterMl, doseMcg]);

  const inputClass = "w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none transition-colors";
  const labelClass = "text-zinc-500 text-xs uppercase tracking-wider mb-1.5 block";

  if (compact) {
    return (
      <div className="bg-zinc-900/80 border border-zinc-800 p-6 my-8" data-testid="peptide-calc-compact">
        <div className="flex items-center gap-3 mb-4">
          <Calculator className="w-5 h-5 text-[#E63946]" />
          <h4 className="text-white font-heading text-lg">Peptide Dosage Calculator</h4>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className={labelClass}>Vial (mg)</label>
            <input type="number" value={vialMg} onChange={e => { setVialMg(e.target.value); setPreset('Custom'); }} placeholder="5" className={inputClass} data-testid="calc-vial-compact" />
          </div>
          <div>
            <label className={labelClass}>BAC Water (ml)</label>
            <input type="number" value={waterMl} onChange={e => { setWaterMl(e.target.value); setPreset('Custom'); }} placeholder="2" className={inputClass} data-testid="calc-water-compact" />
          </div>
          <div>
            <label className={labelClass}>Dose (mcg)</label>
            <input type="number" value={doseMcg} onChange={e => { setDoseMcg(e.target.value); setPreset('Custom'); }} placeholder="250" className={inputClass} data-testid="calc-dose-compact" />
          </div>
        </div>
        {result && (
          <div className="flex items-center justify-between p-3 bg-[#E63946]/10 border border-[#E63946]/30">
            <span className="text-zinc-400 text-sm">Draw:</span>
            <span className="text-[#E63946] text-xl font-heading">{result.volumeMl.toFixed(3)} ml ({result.units.toFixed(1)} units)</span>
          </div>
        )}
        <Link to="/tools/peptide-calculator" className="text-[#E63946] text-xs mt-3 inline-flex items-center gap-1 hover:underline" data-testid="calc-full-link">
          Full calculator with presets <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="peptide-calculator-full">
      {/* Preset selector */}
      <div className="mb-8">
        <label className={labelClass}>Quick Presets</label>
        <div className="flex flex-wrap gap-2">
          {PEPTIDE_PRESETS.map(p => (
            <button
              key={p.name}
              onClick={() => applyPreset(p.name)}
              className={`px-3 py-1.5 text-xs tracking-wider transition-all border ${
                preset === p.name
                  ? 'bg-[#E63946]/15 border-[#E63946] text-white'
                  : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500'
              }`}
              data-testid={`calc-preset-${p.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Beaker className="w-4 h-4 text-[#E63946]" />
            <label className={labelClass + ' mb-0'}>Peptide in Vial (mg)</label>
          </div>
          <input type="number" value={vialMg} onChange={e => { setVialMg(e.target.value); setPreset('Custom'); }} placeholder="e.g. 5" step="0.1" min="0" className={inputClass} data-testid="calc-vial" />
          <p className="text-zinc-600 text-xs mt-1">Amount of lyophilized peptide</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Droplets className="w-4 h-4 text-[#00F0FF]" />
            <label className={labelClass + ' mb-0'}>BAC Water Added (ml)</label>
          </div>
          <input type="number" value={waterMl} onChange={e => { setWaterMl(e.target.value); setPreset('Custom'); }} placeholder="e.g. 2" step="0.1" min="0" className={inputClass} data-testid="calc-water" />
          <p className="text-zinc-600 text-xs mt-1">Bacteriostatic water volume</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Syringe className="w-4 h-4 text-[#FFD100]" />
            <label className={labelClass + ' mb-0'}>Desired Dose (mcg)</label>
          </div>
          <input type="number" value={doseMcg} onChange={e => { setDoseMcg(e.target.value); setPreset('Custom'); }} placeholder="e.g. 250" step="1" min="0" className={inputClass} data-testid="calc-dose" />
          <p className="text-zinc-600 text-xs mt-1">Per injection dose in micrograms</p>
        </div>
      </div>

      {/* Result */}
      <div className="bg-zinc-900/80 border border-zinc-800 p-8" data-testid="calc-result">
        {result ? (
          <>
            <div className="text-center mb-2">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">You Need to Draw</p>
              <div className="text-5xl text-[#E63946] font-heading mb-1" data-testid="calc-result-ml">{result.volumeMl.toFixed(3)} ml</div>
              <div className="text-2xl text-white font-heading" data-testid="calc-result-units">{result.units.toFixed(1)} units</div>
              <p className="text-zinc-500 text-xs mt-1">on a 100-unit insulin syringe</p>
            </div>
            <SyringeVisual fillPercent={result.fillPercent} />
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-zinc-700">
              <div className="text-center">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Concentration</p>
                <p className="text-white text-lg font-heading">{result.concentration.toFixed(0)} mcg/ml</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Doses Per Vial</p>
                <p className="text-white text-lg font-heading">{Math.floor((parseFloat(vialMg) * 1000) / parseFloat(doseMcg))}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Calculator className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">Enter values above or select a preset to calculate your dosage</p>
          </div>
        )}
      </div>

      <div className="mt-6">
        <PeptiCoolBanner utmContent="peptide_calculator_below_results" compact />
      </div>

      {/* Formula explanation */}
      <div className="mt-8 p-6 bg-zinc-900/50 border border-zinc-800">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-[#00F0FF]" />
          <h4 className="text-white text-sm font-heading">How It Works</h4>
        </div>
        <div className="text-zinc-400 text-sm space-y-2">
          <p><strong className="text-zinc-300">Step 1:</strong> Concentration = (Peptide mg x 1,000) / BAC Water ml = mcg/ml</p>
          <p><strong className="text-zinc-300">Step 2:</strong> Volume to Draw = Desired Dose mcg / Concentration mcg/ml = ml</p>
          <p><strong className="text-zinc-300">Step 3:</strong> Syringe Units = Volume ml x 100 (for 100-unit insulin syringe)</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 border border-zinc-800 text-zinc-600 text-xs leading-relaxed">
        <strong>Disclaimer:</strong> This calculator is for informational and research purposes only. It is not medical advice.
        Always consult a qualified healthcare provider before starting any peptide protocol.
        NinjAthlete is not responsible for the misuse of this tool.
      </div>
    </div>
  );
}

// Full page wrapper
export default function PeptideCalculatorPage() {
  return (
    <main className="pt-20 min-h-screen" data-testid="peptide-calculator-page">
      <section className="container-ninja section-spacing">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 text-xs uppercase tracking-widest bg-[#E63946] text-white font-bold mb-4">
            Research Tool
          </span>
          <h1 className="text-white mb-4">
            Peptide Dosage <span className="text-[#E63946]">Calculator</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Calculate the exact volume to draw based on your vial concentration, reconstitution volume, and desired dose.
          </p>
        </div>
        <PeptideCalculator />
      </section>
    </main>
  );
}
